import { useNavigate } from "react-router-dom";
import DashboardLayoutMain from "../../../layout/dashboard/DashboardLayoutMain";
import React from "react";
import { ALL_ROUTES } from "../../../routes/AllRoutes";
import {
  RazorBadgeTwo,
  RazorButton,
  RazorCopyText,
  RazorNumberFormat,
  RazorTextTrim,
  RazorToolTip,
  RazorGetValue,
} from "../../../components/razor-reuse";
import ExchangeCurrencyText from "../../../components/fragment/cur-text/ExchangeCurrencyText";
import { formatTime, formatToLocalTime } from "../../../utils/Helper";
import { useDispatch, useSelector } from "react-redux";
import {
  approveTransactionPayout,
  getTransactionDetails,
  rejectTransactionPayout,
  retryTransactionPayout,
} from "../../../redux/transaction";
import RootState from "../../../redux/type";
import CustomModal, {
  useDialogToggle,
} from "../../../components/fragment/custom-modal";
import TextColorFragment from "../../../components/fragment/text-color/TextColorFragment";
import { Button } from "../../../components/custom/custom-button";
import { RefreshCcwDot } from "lucide-react";

export default function TransactionDetails() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { openDialog, closeDialog, dialogRef } = useDialogToggle();
  const [note, setNote] = React.useState<string>("");
  const [actionType, setActionType] = React.useState<
    "approve" | "reject" | null
  >(null);

  const {
    loading,
    transactions_details,
    loading_approval,
    loading_rejection,
    loading_retry,
  } = useSelector((state: RootState) => state.transaction);
  const { user } = useSelector((state: RootState) => state.auth);

  // The logged-in admin is a valid approver for this transaction's current
  // approval level if they appear in current_approvers.approvers.
  const isCurrentApprover = (
    transactions_details?.current_approvers?.approvers || []
  ).some((approver: any) => approver?.id === user?.id);

  // The `approval` array isn't guaranteed to arrive sorted by level, so sort
  // it before using it to render level badges or check overall status.
  const approvalLevels = [...(transactions_details?.approval || [])].sort(
    (a: any, b: any) => (a?.current_level || 0) - (b?.current_level || 0),
  );
  const isApprovalRejected = approvalLevels.some(
    (lvl: any) => lvl?.status === "rejected",
  );

  // Multiple approvers can be assigned to the same level — whichever of them
  // acts first decides that level, and the other entries at that level flip
  // to "superseded". Collapse each level down to a single, human-readable
  // step: who approved/rejected it, or who's still pending if undecided.
  const approvalSteps = React.useMemo(() => {
    const groups = new Map<number, any[]>();
    approvalLevels.forEach((entry: any) => {
      const level = entry?.current_level;
      if (level == null) return;
      if (!groups.has(level)) groups.set(level, []);
      groups.get(level)!.push(entry);
    });

    const formatApproverName = (entry: any) =>
      `${entry?.approver?.first_name || ""} ${entry?.approver?.last_name || ""}`.trim() ||
      entry?.approver?.email ||
      "an approver";

    return Array.from(groups.entries())
      .sort(([a], [b]) => a - b)
      .map(([level, entries]) => {
        const decided = entries.find(
          (entry) =>
            entry?.status === "approved" || entry?.status === "rejected",
        );
        if (decided) {
          return {
            level,
            status: decided.status as "approved" | "rejected",
            approverName: formatApproverName(decided),
            actionedAt: decided.actioned_at,
            comment: decided.comment,
          };
        }
        const pendingApprovers = entries
          .filter((entry) => entry?.status === "pending")
          .map(formatApproverName);
        return {
          level,
          status: "pending" as const,
          pendingApprovers,
        };
      });
  }, [approvalLevels]);

  const openActionDialog = (type: "approve" | "reject") => {
    setActionType(type);
    setNote("");
    openDialog();
  };

  const closeActionDialog = () => {
    closeDialog();
    setActionType(null);
    setNote("");
  };

  const cust_id = localStorage?.getItem("cors_key") || "";

  React.useEffect(() => {
    if (cust_id) {
      // fetch details of the transaction using cust_id
      dispatch(getTransactionDetails({ id: cust_id }) as any);
    } else {
      navigate(ALL_ROUTES?.DASHBOARD_TRANSACTION);
    }
  }, [cust_id, dispatch, navigate]);

  const objListToView = [
    {
      key: "sender",
      label: "Sender's Name",
      value: transactions_details?.sender?.first_name
        ? `${transactions_details?.sender?.first_name} ${transactions_details?.sender?.last_name}`
        : transactions_details?.sender?.account_name || "",
    },
    {
      key: "sender_acc",
      label: "Sender's Bank",
      value: transactions_details?.sender?.bank_name || "",
    },
    {
      key: "sender_acc",
      label: "Sender's Account Num",
      value: transactions_details?.sender?.account_number || "",
    },
    {
      key: "tx_reference",
      label: "Transaction Reference",
      value: transactions_details?.sender?.tx_reference || "",
      copy: true,
    },
    {
      key: "recipient_name",
      label: "Receipient’s Name",
      value:
        transactions_details?.beneficiary_name?.trim() ||
        transactions_details?.recipient_name?.trim() ||
        transactions_details?.beneficiary_details?.account_name?.trim() ||
        "-----",
    },
    {
      key: "recipient_email",
      label: "Recipient Email",
      value:
        transactions_details?.beneficiary_details?.interac_email ||
        transactions_details?.beneficiary_details?.email ||
        "---",
    },
    {
      key: "bank_name",
      label: "Recipient Bank",
      value: transactions_details?.beneficiary_details?.bank_name || "",
    },
    {
      key: "recipient_account",
      label: "Recipient Account Num",
      value:
        transactions_details?.beneficiary_details?.bank_account_number ||
        transactions_details?.beneficiary_details?.account_number ||
        "",
    },
    {
      key: "bank_country",
      label: "Bank City",
      value: transactions_details?.beneficiary_details?.bank_country || "",
    },
    {
      key: "bank_city",
      label: "Bank City",
      value: transactions_details?.beneficiary_details?.bank_city || "",
    },
    {
      key: "bank_address",
      label: "Bank Address",
      value: transactions_details?.beneficiary_details?.bank_address || "",
    },
    {
      key: "bank_zip_code",
      label: "Bank Zip Code",
      value: transactions_details?.beneficiary_details?.bank_zip_code || "",
    },
    {
      key: "amount",
      label: "Amount Sent",
      value:
        RazorNumberFormat(transactions_details?.amount, {
          symbol: transactions_details?.source_currency?.code,
          numberAbbreviate: String(transactions_details?.amount)?.length > 8,
        }) || "---",
    },
    {
      key: "credit_amount",
      label: "Credit Amount",
      value:
        RazorNumberFormat(transactions_details?.credit_amount, {
          symbol: transactions_details?.destination_currency?.code || "NIL",
          numberAbbreviate:
            String(transactions_details?.credit_amount)?.length > 8,
        }) || "---",
    },
    {
      key: "phone_number",
      label: "Recipient Telephone",
      value:
        transactions_details?.beneficiary_details?.phone_number ||
        transactions_details?.beneficiary_details?.mobile_number ||
        "",
      copy: true,
    },
    {
      key: "method",
      label: "Transaction Method",
      value: transactions_details?.beneficiary_details?.method || "",
      copy: true,
    },
    {
      key: "iban",
      label: "IBAN",
      value: transactions_details?.beneficiary_details?.iban || "",
      copy: true,
    },
    {
      key: "routing_number",
      label: "Routing Number",
      value: transactions_details?.beneficiary_details?.routing_number || "",
      copy: true,
    },
    {
      key: "sort_code",
      label: "Swift Code",
      value:
        transactions_details?.beneficiary_details?.sort_code ||
        transactions_details?.beneficiary_details?.swift_code ||
        "",
      copy: true,
    },
    {
      key: "street",
      label: "Recipient Street",
      value: transactions_details?.beneficiary_details?.street || "",
      copy: true,
    },
    {
      key: "country",
      label: "Recipient Country",
      value: transactions_details?.beneficiary_details?.country_name || "",
      copy: true,
    },
    {
      key: "state_name",
      label: "Recipient State",
      value: transactions_details?.beneficiary_details?.state_name || "",
      copy: true,
    },
    {
      key: "city_name",
      label: "Recipient City",
      value: transactions_details?.beneficiary_details?.city_name || "",
      copy: true,
    },
    {
      key: "zip_code",
      label: "Zip Code",
      value: transactions_details?.beneficiary_details?.zip_code || "",
      copy: true,
    },
    {
      key: "created_at",
      label: "Transaction Date",
      value: formatTime(transactions_details?.created_at) || "",
    },
    {
      key: "exchange_rate",
      label: "Exchange Rate",
      value:
        RazorNumberFormat(transactions_details?.exchange_rate, {
          symbol: transactions_details?.destination_currency?.code || "NGN",
          numberAbbreviate:
            String(transactions_details?.exchange_rate)?.length > 8,
        }) || "---",
    },
    {
      key: "reward_wallet_amount",
      label: "Reward Wallet Amount",
      value: (
        <TextColorFragment
          text={
            RazorNumberFormat(transactions_details?.reward_wallet_amount, {
              symbol: transactions_details?.source_currency?.code || "NGN",
              numberAbbreviate:
                String(transactions_details?.reward_wallet_amount)?.length > 8,
            }) || "---"
          }
          color="rgba(200, 16, 46, 1)"
        />
      ),
    },
    {
      key: "service_charge",
      label: "Service Charge",
      value:
        RazorNumberFormat(transactions_details?.service_charge, {
          symbol: transactions_details?.source_currency?.code,
          numberAbbreviate:
            String(transactions_details?.service_charge)?.length > 8,
        }) || "0.00",
    },
    {
      key: "sender",
      label: "Currency Type",
      value: (
        <ExchangeCurrencyText
          curOne={transactions_details?.source_currency?.code || "NIL"}
          curTwo={transactions_details?.destination_currency?.code || "NIL"}
          urlOne={""}
          urlTwo={""}
        />
      ),
    },
    {
      key: "sender",
      label: "Status",
      value: (
        <RazorBadgeTwo
          className={
            transactions_details?.status === "failed"
              ? "failed"
              : transactions_details?.status === "successful"
                ? "success"
                : transactions_details?.status === "completed"
                  ? "success"
                  : transactions_details?.status === "pending"
                    ? "pending"
                    : transactions_details?.status === "processing"
                      ? "pending"
                      : transactions_details?.status === "submitted"
                        ? "pending"
                        : ""
          }
          text={transactions_details?.status}
        />
      ),
    },
  ];

  const transactionsLog = [
    {
      title: `Transaction Initiated`,
      description: `User initiates ${transactions_details?.source_currency?.code} to ${transactions_details?.destination_currency?.code} transaction`,
      active:
        transactions_details?.transactionStatus?.transaction_initiated ?? 0,
      time: transactions_details?.transactionStatus?.transaction_initiated_at,
      color: "#189B62",
    },
    {
      title: `${transactions_details?.source_currency?.code} Received`,
      description: `${transactions_details?.source_currency?.code} Payment has been received`,
      active: transactions_details?.transactionStatus?.deposit_recieved ?? 0,
      time: transactions_details?.transactionStatus?.deposit_recieved_at,
      color: "#189B62",
    },
    {
      title: `Transaction Processing`,
      description: `Third party process transaction`,
      active: transactions_details?.transactionStatus?.processing ?? 0,
      time: transactions_details?.transactionStatus?.processing_at,
      color:
        transactions_details?.transactionStatus?.transfer_sent !== 1
          ? "#FFC043"
          : " #189B62",
    },
    {
      title: `Beneficiary Credited`,
      description: `Beneficiary receives ${transactions_details?.destination_currency?.code}`,
      active: transactions_details?.transactionStatus?.transfer_sent ?? 0,
      time: transactions_details?.transactionStatus?.transfer_sent_at,
      color: "#189B62",
    },
    {
      title: `Transaction Completed`,
      description: ``,
      active:
        transactions_details?.transactionStatus?.transfer_completed === 1 ||
        transactions_details?.transactionStatus?.transfer_sent === 1
          ? 1
          : 0,
      time: transactions_details?.transactionStatus?.transfer_completed_at,
      color:
        transactions_details?.transactionStatus?.transfer_sent === 1 &&
        transactions_details?.status === "processing"
          ? "#FFC043"
          : " #189B62",
    },
    {
      title: `Transaction Failed`,
      description: `Transaction has failed`,
      active:
        transactions_details?.transactionStatus?.transfer_completed === 0 &&
        transactions_details?.transactionStatus?.transfer_completed_at
          ? 1
          : 0,
      time: transactions_details?.transactionStatus?.transfer_completed_at,
      color: "#F76659",
    },
  ];

  const filteredLogs = transactionsLog.filter((item) => item?.active !== 0);

  const handleApprove = async () => {
    const data = await dispatch(
      approveTransactionPayout({ id: cust_id, comment: note || null }) as any,
    );
    if (data?.payload?.data?.status) {
      dispatch(getTransactionDetails({ id: cust_id }) as any);
      closeActionDialog();
    }
  };

  const handleReject = async () => {
    if (!note.trim()) return;
    const data = await dispatch(
      rejectTransactionPayout({
        id: cust_id,
        rejection_reason: note.trim(),
      }) as any,
    );
    if (data?.payload?.data?.status) {
      dispatch(getTransactionDetails({ id: cust_id }) as any);
      closeActionDialog();
    }
  };

  const handleRetry = async () => {
    if (!transactions_details?.id) return;
    const data = await dispatch(
      retryTransactionPayout({ id: transactions_details.id }) as any,
    );
    if (data?.payload?.data?.status) {
      dispatch(getTransactionDetails({ id: cust_id }) as any);
    }
  };

  const handleConfirmAction = () => {
    if (actionType === "reject") {
      handleReject();
    } else {
      handleApprove();
    }
  };

  return (
    <DashboardLayoutMain
      back
      onBack={() => navigate(-1)}
      loading={loading}
      title="Transaction Overview"
    >
      {approvalSteps.length > 0 && (
        <div className="px-[18px] pt-[18px] max-w-[1040px]">
          <div className="w-full bg-white border-[0.5px] border[#3C477F1A]">
            <div className="text-[#1e1e1e] font-semibold text-[16px] px-[13px] py-[17px] border-[0.5px] border-[#e6e6e6] flex items-center justify-between">
              <span>Approval Progress</span>
              {!isApprovalRejected &&
                transactions_details?.current_approvers?.level && (
                  <span className="text-[12px] font-medium text-[#D97706] bg-[#FFF7E6] px-[10px] py-[4px] rounded-[999px]">
                    Awaiting Level{" "}
                    {transactions_details.current_approvers.level}
                  </span>
                )}
            </div>
            <div className="relative px-[25px] py-[20px]">
              {approvalSteps.map((step, index) => {
                const color =
                  step.status === "approved"
                    ? "#189B62"
                    : step.status === "rejected"
                      ? "#F76659"
                      : "#FFC043";
                return (
                  <div
                    key={step.level}
                    className="mb-[28px] last:mb-0 flex items-start relative"
                  >
                    <div className="absolute -left-[7px] mt-[2px] items-center h-fit">
                      {index !== 0 && (
                        <div
                          className="w-[2px] h-[52px] mx-auto absolute -top-[100%] -translate-y-3/4 left-1/2 -translate-x-1/2"
                          style={{ backgroundColor: color }}
                        />
                      )}
                      <div
                        className="size-[10px] rounded-full z-[1] relative"
                        style={{ backgroundColor: color }}
                      />
                    </div>
                    <div className="ml-4 flex-1 flex justify-between items-start gap-[4px]">
                      <div>
                        <h3 className="text-[12px] font-medium text-[#758089]">
                          Level {step.level}
                        </h3>
                        <p className="text-[#1E1E1E] font-medium text-[12px]">
                          {step.status === "approved"
                            ? `Approved by ${step.approverName}`
                            : step.status === "rejected"
                              ? `Rejected by ${step.approverName}`
                              : `Awaiting approval — ${
                                  step.pendingApprovers?.join(", ") || "—"
                                }`}
                        </p>
                        {step.comment && (
                          <p className="text-[11px] text-[#A1A1AA] mt-[2px]">
                            “{step.comment}”
                          </p>
                        )}
                      </div>
                      <div className="text-right text-[#758089] text-[12px] shrink-0">
                        {step.actionedAt
                          ? formatTime(step.actionedAt)
                          : "Pending"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <main className="flex max-lg:flex-wrap gap-[12px] p-[18px] max-w-[1040px]">
        <div className="w-full lg:w-[45%] bg-white border-[0.5px] border[#3C477F1A]">
          <div className="text-[#1e1e1e] font-semibold text-[16px] px-[13px] py-[17px] border-[0.5px] border-[#e6e6e6]">
            Transaction Details
          </div>

          {/* Transaction Details start */}
          <div className="px-[25px] pb-[16px] mt-[16px] space-y-[16px]">
            {RazorGetValue(objListToView, transactions_details)?.map(
              (chi: any, idx) => {
                if (!chi?.value) return;
                return (
                  <div key={idx} className="flex items-center max-w-[420px]">
                    <p className="w-[45%] text-[#758089] text-[12px]">
                      {chi?.label}:
                    </p>
                    <div className="text-[#596080] text-[14px] font-medium">
                      {chi?.copy ? (
                        <RazorCopyText
                          textToCopy={chi?.value}
                          reference={RazorTextTrim(chi?.value, 25)}
                          outerCopy
                        />
                      ) : (
                        RazorTextTrim(chi?.value, 45) || ""
                      )}
                      <RazorToolTip
                        color="black-light"
                        text={chi?.value}
                        position={`top-center`}
                      />
                    </div>
                  </div>
                );
              },
            )}

            {(transactions_details?.status === "processing" ||
              transactions_details?.status === "under_review") &&
              isCurrentApprover &&
              !isApprovalRejected && (
                <div className="flex items-center w-full gap-8 mt-4">
                  <RazorButton
                    onClick={() => openActionDialog("approve")}
                    className="flex-1 !justify-center !py-[12px] !text-green-01 !border !border-solid !border-green-01 !bg-green-02"
                    color={"white-light"}
                  >
                    Approve Payout
                  </RazorButton>

                  <RazorButton
                    onClick={() => openActionDialog("reject")}
                    className="flex-1 !justfify-center !py-[12px] !bg-red-02 !text-red-01 !border !border-red-01"
                    color={"white-light"}
                  >
                    Reject
                  </RazorButton>
                </div>
              )}
          </div>
          {/* Transaction Details end */}
        </div>
        <div className="bg-white border-[0.5px] border[#3C477F1A] w-full lg:w-[55%]">
          <div className="text-[#1e1e1e] font-semibold text-[16px] px-[13px] py-[17px] border-[0.5px] border-[#e6e6e6]">
            Transaction Log
          </div>
          {filteredLogs?.length > 0 ? (
            <div className="relative px-[25px] pb-[16px] mt-[16px]">
              {filteredLogs.map((transaction, index) => {
                return (
                  <div
                    key={index}
                    className="mb-[36px] last:mb-0 flex items-start relative"
                  >
                    <div className="absolute -left-[7px] mt-[2px] items-center h-fit">
                      {index !== 0 && (
                        <div
                          className="w-[2px] h-[68px] mx-auto absolute -top-[110%] -translate-y-3/4 left-1/2 -translate-x-1/2"
                          style={{
                            backgroundColor: transaction.color,
                          }}
                        />
                      )}
                      <div
                        className="size-[10px] rounded-full z-[1] relative"
                        style={{
                          backgroundColor: transaction.color,
                        }}
                      />
                    </div>
                    <div className="ml-4 flex-1 flex justify-between items-center gap-[4px]">
                      <div>
                        <h3 className="text-[12px] font-medium text-[#758089]">
                          {transaction.title}
                        </h3>
                        <p className="text-[#1E1E1E] font-medium text-[12px]">
                          {transaction.description}
                        </p>
                      </div>
                      <div className="text-right text-[#758089] text-sm md:text-base md:ml-4">
                        {transaction.time
                          ? formatToLocalTime(transaction.time ?? "")
                          : "--- ---"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-[#758089] text-[14px] font-medium px-[25px] pb-[16px] mt-[16px] h-[120px] grid place-content-center">
              No Transaction Log Available
            </div>
          )}

          {["failed", "reversed", "processing"]?.includes(
            transactions_details?.status,
          ) &&
            !isApprovalRejected &&
            user?.permissions?.includes("retry_transaction") && (
              <div className="px-[15px] mt-[30px]">
                <Button
                  isLoading={loading_retry}
                  disabled={loading_retry}
                  onClick={handleRetry}
                  className="min-w-[150px]"
                >
                  <RefreshCcwDot />
                  Retry Transaction
                </Button>
              </div>
            )}
        </div>
      </main>
      <CustomModal
        ref={dialogRef}
        onClose={closeActionDialog}
        headerTitle={
          actionType === "reject" ? "Reject Transaction" : "Approve Transaction"
        }
        containerClass="!w-[600px]"
      >
        <section className="my-[14px] mx-auto p-[10px] text-[16px] min-h-[78px] w-[537px] !justify-center bg-[#F7F7F7] text-[#758089]">
          <div className="bg-[#FFFFFF] rounded-[16px] p-[20px] mb-[24px]">
            <div className="flex justify-between items-start mb-[12px]">
              <span className="text-[13px] font-medium text-[#758089]">
                {transactions_details?.tx_reference ||
                  transactions_details?.reference ||
                  "---"}
              </span>
              <RazorBadgeTwo
                className={
                  isApprovalRejected
                    ? "failed"
                    : transactions_details?.current_approvers?.level
                      ? "pending"
                      : transactions_details?.status === "pending" ||
                          transactions_details?.status === "processing"
                        ? "pending"
                        : "success"
                }
                text={
                  isApprovalRejected
                    ? "Rejected"
                    : transactions_details?.current_approvers?.level
                      ? `Awaiting Level ${transactions_details.current_approvers.level}`
                      : transactions_details?.status || "Awaiting Approval"
                }
              />
            </div>
            <h2 className="text-[24px] leading-8 font-semibold text-[#1D2023] tracking-tight mb-[8px]">
              {RazorNumberFormat(transactions_details?.amount, {
                symbol: transactions_details?.source_currency?.code || "GBP",
                numberAbbreviate: false,
              }) || "---"}
            </h2>
            <p className="text-[14px] font-medium text-[#586069]">
              {transactions_details?.sender?.first_name
                ? `${transactions_details?.sender?.first_name} ${transactions_details?.sender?.last_name}`
                : transactions_details?.sender?.account_name || "---"}
            </p>
          </div>
          <div>
            <label className="block text-[12px] font-normal text-[#586069] mb-[8px]">
              {actionType === "reject"
                ? "Rejection reason (required)"
                : "Approval note — optional"}
            </label>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note for the record..."
              className="w-[100%] p-[12px] text-[14px] bg-[#FFFFFF] rounded-[8px] outline-none focus:border-[#734792] transition-colors resize-none placeholder:text-[#B3B3B3]"
            />
          </div>
          <div className="flex justify-end items-center !mb-[10px] gap-[16px] w-full">
            <RazorButton
              color="white-light"
              className="btn !w-[160px] !justify-center !mt-[24px] !py-[12px] !px-[24px] !text-pry"
              label="Cancel"
              disabled={loading_approval || loading_rejection}
              onClick={closeActionDialog}
            />
            <RazorButton
              color="black-light"
              className="btn !w-[160px] !justify-center !mt-[24px] !py-[12px] !px-[24px]"
              label={
                actionType === "reject"
                  ? "Confirm Rejection"
                  : "Confirm Approval"
              }
              loading={
                actionType === "reject" ? loading_rejection : loading_approval
              }
              disabled={actionType === "reject" && !note.trim()}
              onClick={handleConfirmAction}
            />
          </div>
        </section>
      </CustomModal>
    </DashboardLayoutMain>
  );
}
